import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Checkbox, Divider, FormControlLabel, Stack, SxProps, Theme } from '@mui/material';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

export interface RHFDateWithCheckboxProps
  extends Omit<DatePickerProps<Dayjs>, 'value' | 'onChange'> {
  /** 날짜 필드명 */
  dateName: string;
  /** 체크박스 필드명 */
  checkboxName: string;
  /** 체크박스 라벨 */
  checkboxLabel?: string;
  /** 체크박스 체크 시 DatePicker 라벨 */
  checkedLabel?: string;
  /** 읽기 전용 여부 */
  readOnly?: boolean;
  /** 날짜 초기화 가능 여부 */
  clearable?: boolean;
  /** 컨테이너 sx */
  sx?: SxProps<Theme>;
}

/**
 * 날짜 선택 + 체크박스 조합 컴포넌트
 * - 날짜 선택과 완료 여부를 하나의 플로우로 표시
 */
export default function RHFDateWithCheckbox({
  dateName,
  checkboxName,
  checkboxLabel = '',
  checkedLabel,
  readOnly = false,
  clearable = false,
  sx,
  ...other
}: RHFDateWithCheckboxProps) {
  const { control } = useFormContext();
  const isChecked = useWatch({ control, name: checkboxName });

  // 체크 여부에 따라 라벨 결정
  const dateLabel = isChecked && checkedLabel ? checkedLabel : other.label;

  return (
    <Controller
      name={dateName}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // null 허용: field.value가 null이면 null 유지
        const dateValue =
          field.value != null && dayjs(field.value).isValid() ? dayjs(field.value) : null;

        return (
          <DatePicker
            {...field}
            label={dateLabel}
            value={dateValue}
            sx={sx}
            onChange={(newValue) => {
              // null이거나 invalid date면 null로 저장
              if (!newValue || !dayjs(newValue).isValid()) {
                field.onChange(null);
              } else {
                field.onChange(dayjs(newValue).format('YYYY-MM-DD'));
              }
            }}
            readOnly={readOnly}
            slots={{
              inputAdornment: (props) => (
                <Stack
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  {props.children}
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    sx={{
                      paddingLeft: 1,
                    }}
                  />
                  <Controller
                    name={checkboxName}
                    control={control}
                    render={({ field: checkboxField }) => (
                      <FormControlLabel
                        label={checkboxLabel}
                        labelPlacement="start"
                        sx={{
                          mx: 0,
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: 'text.secondary',
                          flexShrink: 0,
                        }}
                        control={
                          <Checkbox
                            {...checkboxField}
                            checked={checkboxField.value || false}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => checkboxField.onChange(e.target.checked)}
                            disabled={readOnly}
                            size="small"
                          />
                        }
                      />
                    )}
                  />
                </Stack>
              ),
            }}
            slotProps={{
              field: { clearable: clearable && !readOnly },
              textField: {
                label: dateLabel,
                error: Boolean(error),
                helperText: error ? error.message : undefined,
                size: 'medium',
              },
            }}
            {...other}
          />
        );
      }}
    />
  );
}
